/**
 * R2-style 名称 + カナ: IME auto-kana (jquery.autoKana style), name blur (ANK), katakana-only カナ (CK).
 * Spread `nameInputProps` / `kanaInputProps` on your inputs; you own `value`, labels, layout, errors.
 */

import {
  useCallback,
  useMemo,
  useRef,
  type ChangeEvent,
  type CompositionEvent,
  type FocusEvent,
  type MutableRefObject,
} from 'react';
import {
  ensureKatakanaOnly,
  halfWidthToFullWidthKana,
  hiraganaToKatakana,
  normalizeJapaneseNameOnBlur,
} from '@/utils/japaneseText';

// --- IME core (internal; composed by useJapaneseNameKanaFields) ---

type UseNameToKanaAutoFillOptions = {
  setKanaValue: (kana: string) => void;
  isComposingRef?: MutableRefObject<boolean>;
  /** See `UseJapaneseNameKanaFieldsOptions.keepExistingKana`. */
  keepExistingKana?: boolean;
};

const hasJapaneseChar = (value: string): boolean =>
  /[\u3040-\u30ff\u31f0-\u31ff\u4e00-\u9fff々〆ヵヶ]/u.test(value);

const hasKanaReading = (value: string): boolean => /[\u3040-\u30ff\u31f0-\u31ff]/u.test(value);
const hasKanji = (value: string): boolean => /[\u4E00-\u9FFF]/.test(value);
/** Keep kana from IME reading; preserve ASCII / full-width space (strip was dropping 姓 名 gaps). */
const keepKanaCharsOnly = (value: string): string =>
  (value.match(/[\u3040-\u30ff\u31f0-\u31ff \u3000]/gu) ?? []).join('');

const isHiragana = (code: number): boolean => code >= 0x3040 && code <= 0x309f;
const isKatakana = (code: number): boolean => code >= 0x30a0 && code <= 0x30ff;
const isKanaSpace = (ch: string): boolean => ch === ' ' || ch === '\u3000';

/**
 * Compute the kana insertion position by suffix-matching the name text after
 * the cursor against the end of the kana string.  Hiragana↔katakana pairs and
 * spaces are matched 1:1 from the tail; the scan stops at any kanji or
 * unmatchable character, giving the correct split point even when kanji before
 * the cursor caused name/kana lengths to diverge.
 */
function computeKanaInsertPos(nameValue: string, cursorPos: number, kanaValue: string): number {
  const nameAfter = nameValue.slice(cursorPos);
  let nIdx = nameAfter.length - 1;
  let kIdx = kanaValue.length - 1;

  while (nIdx >= 0 && kIdx >= 0) {
    const nCh = nameAfter[nIdx];
    const kCh = kanaValue[kIdx];
    const nCode = nCh.charCodeAt(0);

    if (isHiragana(nCode)) {
      // Hiragana in name should match katakana (+0x60) in kana
      if (kCh === String.fromCharCode(nCode + 0x60)) {
        nIdx--;
        kIdx--;
      } else {
        break;
      }
    } else if (isKatakana(nCode)) {
      if (kCh === nCh) {
        nIdx--;
        kIdx--;
      } else {
        break;
      }
    } else if (isKanaSpace(nCh)) {
      if (isKanaSpace(kCh)) {
        nIdx--;
        kIdx--;
      } else {
        break;
      }
    } else {
      // Kanji or other — stop matching
      break;
    }
  }

  return kIdx + 1;
}

function useNameToKanaAutoFill(options: UseNameToKanaAutoFillOptions) {
  const internalComposingRef = useRef(false);
  const composingRef = options.isComposingRef ?? internalComposingRef;
  const committedKanaRef = useRef('');
  const lastKanaRef = useRef('');
  const hasKanaUpdateInCompositionRef = useRef(false);
  /** Kana insertion position (mapped from name cursor position at composition start). */
  const kanaInsertPosRef = useRef(0);
  /** Last name string used for suffix space → kana mirror (IME reading omits spaces between kanji / kana). */
  const prevNameRef = useRef('');
  /**
   * `keepExistingKana`: the カナ holds a value the auto-fill did not write (loaded,
   * typed or pasted by the user) — leave it alone until it is emptied.
   */
  const keepExistingKanaRef = useRef(options.keepExistingKana ?? false);
  keepExistingKanaRef.current = options.keepExistingKana ?? false;
  const kanaLockedRef = useRef(false);

  const setKanaAndTrack = useCallback(
    (kana: string) => {
      if (keepExistingKanaRef.current && kanaLockedRef.current) return;
      lastKanaRef.current = kana;
      options.setKanaValue(kana);
    },
    [options]
  );

  const syncLastKana = useCallback((kana: string) => {
    if (keepExistingKanaRef.current && kana !== lastKanaRef.current) {
      kanaLockedRef.current = kana !== '';
    }
    lastKanaRef.current = kana;
    if (kana === '') {
      prevNameRef.current = '';
      kanaLockedRef.current = false;
    }
  }, []);

  const handleNameCompositionStart = useCallback(
    (nameValue: string, cursorPos?: number) => {
      composingRef.current = true;
      committedKanaRef.current = lastKanaRef.current;
      hasKanaUpdateInCompositionRef.current = false;
      kanaInsertPosRef.current =
        cursorPos != null
          ? computeKanaInsertPos(nameValue, cursorPos, lastKanaRef.current)
          : lastKanaRef.current.length;
    },
    [composingRef]
  );

  const handleNameCompositionUpdate = useCallback(
    (reading: string) => {
      const kanaOnlyReading = keepKanaCharsOnly(reading);
      const kanaReading = halfWidthToFullWidthKana(hiraganaToKatakana(kanaOnlyReading));
      const pos = kanaInsertPosRef.current;
      const before = committedKanaRef.current.slice(0, pos);
      const after = committedKanaRef.current.slice(pos);
      const nextKana = `${before}${kanaReading}${after}`;
      const hasOnlySpaces = /^[\s\u3000]+$/.test(reading);
      if (!hasKanji(reading) && (hasKanaReading(kanaOnlyReading) || hasOnlySpaces)) {
        hasKanaUpdateInCompositionRef.current = true;
        setKanaAndTrack(nextKana);
      } else if (hasKanji(reading)) {
        setKanaAndTrack(lastKanaRef.current);
      } else if (hasKanaUpdateInCompositionRef.current) {
        // Composition cleared (e.g. user deleted all composed text) — revert
        setKanaAndTrack(committedKanaRef.current);
        hasKanaUpdateInCompositionRef.current = false;
      }
    },
    [setKanaAndTrack]
  );

  const handleNameCompositionEnd = useCallback(
    (inputValue: string, currentKanaValue: string) => {
      composingRef.current = false;
      const raw = String(inputValue ?? '');
      if (raw.trim() === '') {
        setKanaAndTrack('');
        prevNameRef.current = '';
        return;
      }
      if (!hasJapaneseChar(raw)) {
        prevNameRef.current = raw;
        return;
      }

      const kanaToPreserve = hasKanaUpdateInCompositionRef.current
        ? lastKanaRef.current || currentKanaValue
        : '';
      if (kanaToPreserve) {
        setKanaAndTrack(kanaToPreserve);
        prevNameRef.current = raw;
        return;
      }
      setKanaAndTrack(lastKanaRef.current || currentKanaValue);
      prevNameRef.current = raw;
    },
    [setKanaAndTrack]
  );

  const handleNameChange = useCallback(
    (value: string) => {
      const current = String(value ?? '');
      if (!composingRef.current && current.trim() === '') {
        setKanaAndTrack('');
        prevNameRef.current = '';
        return;
      }
      if (!composingRef.current) {
        const prev = prevNameRef.current;
        if (current.length > prev.length) {
          // Find the insertion point by comparing from the start
          let insertPos = 0;
          while (insertPos < prev.length && prev[insertPos] === current[insertPos]) {
            insertPos++;
          }
          const insertedLen = current.length - prev.length;
          const inserted = current.slice(insertPos, insertPos + insertedLen);
          if (inserted && /^[\s\u3000]+$/.test(inserted)) {
            const k = lastKanaRef.current;
            const kanaPos = computeKanaInsertPos(prev, insertPos, k);
            const spaces = inserted.replace(/ /g, '\u3000');
            setKanaAndTrack(k.slice(0, kanaPos) + spaces + k.slice(kanaPos));
          }
        } else if (current.length < prev.length) {
          // Characters were deleted — remove corresponding kana via suffix-matching
          let deletePos = 0;
          while (deletePos < current.length && prev[deletePos] === current[deletePos]) {
            deletePos++;
          }
          const k = lastKanaRef.current;
          const kanaDelStart = computeKanaInsertPos(prev, deletePos, k);
          const kanaDelEnd = computeKanaInsertPos(current, deletePos, k);
          if (kanaDelStart < kanaDelEnd) {
            setKanaAndTrack(k.slice(0, kanaDelStart) + k.slice(kanaDelEnd));
          }
        }
        prevNameRef.current = current;
      }
    },
    [composingRef, setKanaAndTrack]
  );

  const handleKanaManualChange = useCallback((value: string) => {
    if (keepExistingKanaRef.current) kanaLockedRef.current = value !== '';
    lastKanaRef.current = value;
  }, []);

  return {
    composingRef,
    syncLastKana,
    handleNameCompositionStart,
    handleNameCompositionUpdate,
    handleNameCompositionEnd,
    handleNameChange,
    handleKanaManualChange,
  };
}

// --- Public: full name + kana pair ---

export type UseJapaneseNameKanaFieldsOptions = {
  nameKanaValue: string;
  setNameValue: (value: string) => void;
  setKanaValue: (value: string) => void;
  maxLength?: number;
  /** Cap for the name field when it differs from the カナ field. Defaults to `maxLength`. */
  nameMaxLength?: number;
  /** Limit of the kana field when it differs from the name's. Defaults to `maxLength`. */
  kanaMaxLength?: number;
  onClearNameError?: () => void;
  onClearKanaError?: () => void;
  /** When true, skip ensureKatakanaOnly conversion on blur / compositionEnd for the kana field. Defaults to true. */
  disableBlurKanaConvert?: boolean;
  /** When false, skip half→full-width alphanumeric conversion on name blur (use 'SK' instead of 'SANK'). Defaults to true. */
  fullWidthAlphaNumOnBlur?: boolean;
  /**
   * When true, a カナ that already holds a value the auto-fill did not write
   * (loaded with the record, typed or pasted) is not overwritten by typing the
   * name, until the カナ is emptied. Defaults to false.
   */
  keepExistingKana?: boolean;
};

export function useJapaneseNameKanaFields(options: UseJapaneseNameKanaFieldsOptions) {
  const {
    nameKanaValue,
    setNameValue,
    setKanaValue,
    maxLength = 255,
    nameMaxLength = maxLength,
    kanaMaxLength,
    onClearNameError,
    onClearKanaError,
    disableBlurKanaConvert = true,
    fullWidthAlphaNumOnBlur = true,
    keepExistingKana = false,
  } = options;

  const kanaLimit = kanaMaxLength ?? maxLength;
  const clampKana = useCallback(
    (s: string) => (s.length <= kanaLimit ? s : s.slice(0, kanaLimit)),
    [kanaLimit]
  );

  const clampName = useCallback(
    (s: string) => (s.length <= nameMaxLength ? s : s.slice(0, nameMaxLength)),
    [nameMaxLength]
  );

  const {
    composingRef,
    syncLastKana,
    handleNameCompositionStart,
    handleNameCompositionUpdate,
    handleNameCompositionEnd,
    handleNameChange,
    handleKanaManualChange,
  } = useNameToKanaAutoFill({ setKanaValue, keepExistingKana });

  const nameInputProps = useMemo(
    () => ({
      onCompositionStart: (e: CompositionEvent<HTMLInputElement>) => {
        const el = e.target as HTMLInputElement;
        handleNameCompositionStart(el.value ?? '', el.selectionStart ?? undefined);
      },
      onCompositionUpdate: (e: CompositionEvent<HTMLInputElement>) => {
        handleNameCompositionUpdate(e.data ?? '');
      },
      onCompositionEnd: (e: CompositionEvent<HTMLInputElement>) => {
        const inputValue = (e.target as HTMLInputElement).value ?? '';
        handleNameCompositionEnd(inputValue, nameKanaValue || '');
      },
      onBlur: (e: FocusEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setNameValue(
          normalizeJapaneseNameOnBlur(raw, nameMaxLength, fullWidthAlphaNumOnBlur ? 'SANK' : 'SK')
        );
      },
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        onClearNameError?.();
        const next = clampName(raw);
        setNameValue(next);
        handleNameChange(next);
      },
    }),
    [
      handleNameCompositionStart,
      handleNameCompositionUpdate,
      handleNameCompositionEnd,
      handleNameChange,
      setNameValue,
      nameKanaValue,
      nameMaxLength,
      clampName,
      onClearNameError,
      fullWidthAlphaNumOnBlur,
    ]
  );

  const kanaInputProps = useMemo(
    () => ({
      onCompositionStart: () => {
        composingRef.current = true;
      },
      onCompositionEnd: (e: CompositionEvent<HTMLInputElement>) => {
        composingRef.current = false;
        const inputValue = (e.target as HTMLInputElement).value ?? '';
        const cleanedValue = disableBlurKanaConvert ? inputValue : ensureKatakanaOnly(inputValue);
        const next = clampKana(cleanedValue);
        setKanaValue(next);
        handleKanaManualChange(next);
      },
      onBlur: (e: FocusEvent<HTMLInputElement>) => {
        if (!composingRef.current) {
          const raw = e.target.value;
          const cleanedValue = disableBlurKanaConvert ? raw : ensureKatakanaOnly(raw);
          const next = clampKana(cleanedValue);
          setKanaValue(next);
          syncLastKana(next);
        }
      },
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        onClearKanaError?.();
        const next = clampKana(raw);
        setKanaValue(next);
        syncLastKana(next);
        if (!composingRef.current) {
          handleKanaManualChange(next);
        }
      },
    }),
    [composingRef, syncLastKana, handleKanaManualChange, setKanaValue, clampKana, onClearKanaError, disableBlurKanaConvert]
  );

  return { nameInputProps, kanaInputProps, maxLength, nameMaxLength, syncLastKana };
}

export type UseJapaneseNameKanaFieldsResult = ReturnType<typeof useJapaneseNameKanaFields>;
